import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationHelperService } from 'libs/core/src/lib/service/navigation/navigation-helper.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { TranslateService } from '@ngx-translate/core';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { CountdownComponentService } from 'libs/core/src/lib/service/countdown.service';
import { MessageService } from 'libs/core/src/lib/service/message.service';
import { ResponseValidatorService } from 'libs/core/src/lib/service/validator/response-validator.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { BasketDataProvider } from 'libs/core/src/lib/data-provider/basket.data-provider';
import { GoogleTagManagerService } from 'libs/core/src/lib/service/analytics-services/google-tag-manager.service';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { cinema1ValidationPattern } from 'libs/core/src/app.const';
import { TheSameFormValidator } from 'libs/core/src/lib/tool/form/validator/the-same.form-validator';
import { UserDataProvider } from 'libs/core/src/lib/data-provider/user.data-provider';
import { AgreementAggregationViewModel } from 'libs/core/src/lib/model/view-model/agreement/agreement-aggregation.view.model';
import { UserViewModel } from 'libs/core/src/lib/model/view-model/user/user.view.model';
import { LoginHelper } from 'libs/core/src/lib/helper/login.helper';
import { HttpErrorResponse } from '@angular/common/http';
import { filter, switchMap, take } from 'rxjs';
import { UserLoginRequestModel } from 'libs/core/src/lib/model/request/user-login.request.model';
import { UserSetpasswordRequestModel } from 'libs/core/src/lib/model/request/user-setpassword.request.model';
import { NavigationService } from 'libs/core/src/lib/service/navigation/navigation.service';
import { ErrorHandlerService } from 'libs/core/src/lib/service/error-handler/error-handler.service';
import { AgreementViewModel } from '@lib/core';

export enum PersonalStateEnum {
  Initial = 'initial',
  Success = 'success',
  Failed = 'failed'
}

@Component({ standalone: false, selector: 'app-user-register', templateUrl: './user-register.component.html' })
export class CustomUserRegisterComponent implements OnInit {
  public displayMode: 'register' | 'resetPassword' | 'setPassword' | 'confirmRegistration';

  public state = PersonalStateEnum.Initial;
  public stateEnum: typeof PersonalStateEnum = PersonalStateEnum;

  public registerForm: FormGroup = null;
  public setPasswordForm: FormGroup = null;
  public resetPasswordForm: FormGroup = null;

  public errorCode: string = null;

  private userLoginRequestModel: UserLoginRequestModel = null;

  public registerFormErrors: Object = null;
  private token: string = null;
  public agreements: AgreementViewModel[] = [];

  get agreementsFormArray(): FormArray {
    return this.registerForm?.get('agreements') as FormArray;
  }

  public constructor(
    protected responseValidator: ResponseValidatorService,
    protected orderDataProvider: OrderDataProvider,
    protected orderStateService: OrderStateService,
    protected navigationHelper: NavigationHelperService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected countdownComponentService: CountdownComponentService,
    protected messageService: MessageService,
    protected translate: TranslateService,
    protected authStateService: AuthStateService,
    protected basketDataProvider: BasketDataProvider,
    protected googleTagManagerService: GoogleTagManagerService,
    protected loaderService: LoaderService,
    protected userDataProvider: UserDataProvider,
    private navigationService: NavigationService,
    private errorHandlerService: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    this.loaderService.hideLoader(LoaderEnum.MAIN);

    this.route.params.subscribe((params) => {
      this.displayMode = params['displaymode'];
      switch (this.displayMode) {
        case 'register':
          this.userDataProvider.getAgreements().subscribe((agreements: AgreementAggregationViewModel) => {
            this.agreements = agreements.marketingAgreements ?? [];
            this.registerForm = this.getRegisterForm({ agreements: agreements.marketingAgreements });
          });
          break;
        case 'resetPassword':
          this.resetPasswordForm = this.getResetPasswordForm();
          break;
        case 'setPassword':
          this.setPasswordForm = this.getSetPasswordForm(params['token']);
          break;
        case 'confirmRegistration':
          this.userDataProvider.registrationConfirm(params['token']).subscribe({
            next: (res) => {
              this.state = PersonalStateEnum.Success;
            },
            error: (error) => {
              this.state = PersonalStateEnum.Failed;
            }
          });
          break;
      }
    });
  }

  public getRegisterForm(data: any): FormGroup {
    const group = new FormGroup(
      {
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.pattern(cinema1ValidationPattern.email)]),
        password: new FormControl('', [
          Validators.required,
          Validators.pattern(cinema1ValidationPattern.password) // 100 - SOAP limit
        ]),
        repeatPassword: new FormControl('', [Validators.required]),
        birthday: new FormControl(''),
        agreements: new FormArray([])
      },
      {
        validators: [new TheSameFormValidator('password', 'repeatPassword', 'notMatch').getValidator()]
      }
    );

    if (data.agreements?.length) {
      data.agreements.forEach((agreement: AgreementViewModel) => {
        const formControl = new FormControl(false);
        if (agreement.required) {
          formControl.setValidators(Validators.requiredTrue);
        }
        (group.get('agreements') as FormArray).push(formControl);
      });
    }

    return group;
  }

  public submitForm(form: 'registerForm' | 'resetPasswordForm' | 'setPasswordForm') {
    this.errorCode = null;

    const f = this[form] as FormGroup;
    f.updateValueAndValidity();

    if (f.valid) {
      this.loaderService.showLoader(LoaderEnum.MAIN);

      if (form === 'registerForm') {
        const formValue = f.value;
        const selectedAgreementIds: string[] = formValue.agreements
          .map((checked: boolean, i: number) => (checked ? this.agreements[i].id : null))
          .filter((id: string | null) => id !== null);

        const requestModel: UserViewModel = Object.assign(new UserViewModel(), {
          ...formValue,
          agreements: selectedAgreementIds
        });
        this.userDataProvider.create(requestModel).subscribe({
          next: (user) => {
            this.userLoginRequestModel = Object.assign(new UserLoginRequestModel(), {
              username: user.email,
              password: requestModel['password']
            });

            this.loaderService.hideLoader(LoaderEnum.MAIN);
            this.state = PersonalStateEnum.Success;
            this.resetForm(form);
          },
          error: (e: HttpErrorResponse) => {
            this.errorCode = this.errorHandlerService.getCode(e);
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          }
        });
      } else if (form === 'resetPasswordForm') {
        const formData = Object.assign({}, f.value);
        this.userDataProvider.remindPassword(formData['email']).subscribe({
          next: (response) => {
            this.loaderService.hideLoader(LoaderEnum.MAIN);
            this.state = PersonalStateEnum.Success;
            this.resetForm(form);
          },
          error: (e: HttpErrorResponse) => {
            this.errorCode = this.errorHandlerService.getCode(e);
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          }
        });
      } else if (form === 'setPasswordForm') {
        const formData = Object.assign({}, f.value);

        const requestModel = new UserSetpasswordRequestModel();
        requestModel.passwordToken = formData['token'];
        requestModel.password = formData['password'];

        this.userDataProvider.setPassword(requestModel).subscribe({
          next: (response) => {
            this.loaderService.hideLoader(LoaderEnum.MAIN);
            this.clearTokenQuery();
            this.state = PersonalStateEnum.Success;
            this.resetForm(form);
          },
          error: (e: HttpErrorResponse) => {
            this.errorCode = this.errorHandlerService.getCode(e);
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          }
        });
      }
    } else {
      (this[form] as FormGroup).markAsTouched();
    }
  }

  resetForm(form: 'registerForm' | 'resetPasswordForm' | 'setPasswordForm') {
    this[form].reset();
  }

  public getResetPasswordForm(): FormGroup {
    const group = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(cinema1ValidationPattern.email)])
    });

    return group;
  }

  public getSetPasswordForm(token: string): FormGroup {
    const group = new FormGroup(
      {
        token: new FormControl(token),
        password: new FormControl('', [
          Validators.required,
          Validators.pattern(cinema1ValidationPattern.password) // 100 - SOAP limit
        ]),
        repassword: new FormControl('', [Validators.required])
      },
      {
        validators: [new TheSameFormValidator('password', 'repassword', 'notMatch').getValidator()]
      }
    );

    return group;
  }

  public fieldIsInvalid(name: string, form: 'registerForm' | 'setPasswordForm' | 'resetPasswordForm'): boolean {
    const field = this[form].get(name);
    return field.invalid && (field.touched || this[form].touched);
  }

  public passwordIsValid(form: 'registerForm' | 'setPasswordForm'): boolean {
    return LoginHelper.passwordIsValid(this[form].get('password'), new FormControl());
  }

  public tryLoginAndGoForward() {
    if (!this.userLoginRequestModel) {
      return;
    }

    this.authStateService.login(this.userLoginRequestModel).subscribe({
      next: (user) => {
        this.authStateService.state$
          .pipe(
            filter(() => this.authStateService.userIsLoggedAndTokenIsValid()),
            switchMap(() => this.orderStateService.state$),
            take(1)
          )
          .subscribe((o) => {
            this.loaderService.hideLoader(LoaderEnum.MAIN);

            if (o && (!o.userEmail || o.userEmail === '')) {
              this.orderDataProvider.updateOrderUserData(o.cinemaId, o, user.toApiModel()).subscribe(() => this.navigationService.back());
            } else {
              this.navigationService.back();
            }
          });
      },
      error: (e: HttpErrorResponse) => {
        this.loaderService.hideLoader(LoaderEnum.MAIN);
        this.errorCode = LoginHelper.getErrorMessage(this.errorHandlerService.getCode(e));
      }
    });
  }

  stateBack() {
    if (this.state === PersonalStateEnum.Failed) {
      this.state = PersonalStateEnum.Initial;
      this.errorCode = null;
    }
  }

  private clearTokenQuery() {
    window.history.replaceState({}, '', '/');
  }

  setPasswordButtonClick() {
    if (this.state === PersonalStateEnum.Success) {
      this.navigationService.goToHomePage(true);
    } else {
      this.navigationService.goToHomePage();
    }
  }
}
